'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';

import { motion } from 'motion/react';
import classNames from 'classnames';

import { ISpecialistPreview } from 'app/interfaces/specialists';
import { defaultProcessText } from 'app/utility/textProcessing';
import Shading from 'app/components/common/Shading';
import ImageBg from 'app/components/common/ImageBg';

export default function SpecialistFeaturedList({
  items
}: {
  items: ISpecialistPreview[];
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const itemsWithImageDetail = useMemo(() => {
    return items.filter(item => !!item.image_work?.[0]?.url);
  }, [items]);
  const activeItem = useMemo(() => {
    return items[activeIndex];
  }, [items, activeIndex]);

  const activeItemName = useMemo(() => {
    return activeItem?.name?.split(' ').join('<br>') || '';
  }, [activeItem]);

  if (!itemsWithImageDetail.length) return null;

  return (
    <div className="relative h-[600px] xl:h-svh bg-graphite overflow-hidden isolate">
      {items.map((item, index) => {
        const img = item.image_work?.[0];
        // Фон индексируем по полному массиву items — так же, как activeIndex
        // (он ставится из превью-мапы по items). Иначе индексы расходятся,
        // и при клике показывается фон другого специалиста.
        if (!img?.url) return null;
        return (
          <motion.div
            key={index}
            initial="hidden"
            animate={activeIndex === index ? 'visible' : 'hidden'}
            transition={{ duration: 0.5 }}
            variants={{
              hidden: { opacity: 0, scale: 1.05 },
              visible: { opacity: 1, scale: 1 }
            }}
            className="absolute inset-0"
          >
            <ImageBg
              src={img.url}
              alt={img.alt || ''}
              placeholderDataURL={img.placeholder_data_url}
              fill
              className="object-cover rounded"
            />
          </motion.div>
        );
      })}
      <Shading className="bg-black-40" />
      <div className="absolute inset-0 wrapper-main wrapper-max pt-20 pb-3 md:pt-8 md:pb-5 xl:py-10">
        <div className="w-full h-full flex flex-col justify-between text-white">
          <div className="flex flex-col gap-4 text-h2 md:grid md:grid-cols-12 md:gap-3 xl:gap-4">
            <div
              className="md:col-span-2 md:col-start-4"
              dangerouslySetInnerHTML={{
                __html: activeItemName ? defaultProcessText(activeItemName) : ''
              }}
            ></div>
            <div
              className="md:col-span-4"
              dangerouslySetInnerHTML={{
                __html: activeItem?.grade
                  ? defaultProcessText(activeItem.grade)
                  : ''
              }}
            ></div>
          </div>
          <div className="grid grid-cols-6 md:grid-cols-12 gap-2 md:gap-3 xl:gap-4 md:items-end">
            <div className="text-h2 col-span-2 md:col-span-3 md:col-start-4">
              тренерский
              <br />
              состав
            </div>
            <div className="grid grid-cols-4 gap-x-2 gap-y-3 md:gap-x-3 xl:gap-x-4 col-span-4 md:col-start-7">
              {items.map((item, index) => {
                const imgFirst = item.image_preview?.[0];
                const imgSecond = item.image_preview?.[1];
                if (!imgFirst?.url) return null;
                return (
                  <div
                    key={index}
                    className="cursor-pointer group"
                    onClick={() => setActiveIndex(index)}
                  >
                    <div className="relative pb-[150%] rounded bg-graphite overflow-hidden isolate">
                      <div
                        className={classNames(
                          'absolute inset-0 rounded transition-opacity duration-300',
                          activeIndex === index ? 'opacity-100' : 'opacity-70'
                        )}
                      >
                        <ImageBg
                          src={imgFirst.url}
                          alt={imgFirst.alt || ''}
                          placeholderDataURL={imgFirst.placeholder_data_url}
                          fill
                          className="object-cover rounded"
                        />
                      </div>

                      {!!imgSecond?.url && (
                        <div className="absolute inset-0 transition-opacity duration-400 hidden with-hover:block opacity-0 hover:opacity-100">
                          <ImageBg
                            src={imgSecond.url}
                            alt={imgSecond.alt || ''}
                            placeholderDataURL={imgSecond.placeholder_data_url}
                            fill
                            className="object-cover rounded"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="col-span-6 md:col-span-2 2xl:col-span-1 2xl:col-start-12">
              <Link
                href={'/specialists'}
                className="button-default button-trans w-full"
              >
                ко всем
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
